// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Epdm roofing calculator implementation.
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Apex, Deck } from "./Deck.js"

// @@imports-submodule
import {
    AdhesiveCalculator,
    CornerCalculator,
    DrainCalculator,
    MembraneCalculator,
    RooflightCalculator,
    TapeCalculator,
    TrimCalculator
} from "./calculators/index.js"
import {
    epdmConfig,
    epdmConfigSerialOrder,
    serializeConfig,
    deserializeConfig
} from "./config.js"
import { Stock, Bom } from "./stock/index.js"

// @@imports-utils
import { BitBuffer } from "./utils/index.js"

// @@imports-types
import {
    TAPE_TYPES,
    JOIN_TYPES,
    SUBSTRATE_TYPES,
    ADHESIVE_TYPES,
    MEMBRANE_TYPES,
    MEMBRANES,
    SEAM_DIRECTIONS,
    EXTRA_TYPES,
    Config,
    TRIM_COLORS,
    TRIM_MATERIALS,
    TRIM_TYPES,
    TRIMS,
    EdgeType
} from "./types/index.js"
import { ExtraCalculator } from "./calculators/Extra.js"

// @@body
// Declare module scoped global variable.
const MS_IN_DAY = 86400000
const MM_IN_METRE = 100000

/**
 * @todo Complete method documentation including inline comments, jsdoc
 * annotations, and class description.
 * @todo Check that all error throws use decorated error format.
 * @todo Account for cross seams at the maximum roll length (including overlap
 * if using seam join).
 * @todo Add default extra tape and adhesive coverage allowances to config.
 * @todo Consider implementing straight edge corners more specifically, and
 * implement trim offcuts feature requiring straight joiners.
 * @todo Review documentation/notes on converting deck area units.
 */
class EpdmCalculator {
    requiredBom = new Bom()
    differencesBom = new Bom({ allowNegativeQuantities: true })
    extras = /** @type {Map.<string, number>} */ (new Map())

    /**
     * Instantiate EPDM calculator from a given deck definition and available
     * stock.
     * @param {Deck} deck Deck definition.
     * @param {Stock} stock Available stock.
     * @param {object} obj Optional destructured parameters object.
     * @param {Config} [obj.config] EPDM roofing configuration.
     * @param {boolean} [obj.updateConfig] Should calculator automatically
     * update internal config based on available products for options such as
     * trims and membranes?
     */
    constructor(deck, stock, {
        config = epdmConfig,
        updateConfig = false
    } = {}) {
        // 
        this.deck = deck
        this.stock = stock
        this.config = config

        // 
        if (updateConfig) { this.#updateConfig() }

        // Generate a random quote ID seeded from the current timestamp with
        // extra random entropy.
        const date = Date.now()
        const id = new BitBuffer({ size: 6 })
        id.write(Math.floor(date / MS_IN_DAY), { size: 19 })
        id.write(Math.floor(date % MS_IN_DAY / 1000), { size: 17 })
        id.write(Math.round(Math.random() * 2 ** 12), { size: 12 })
        this.id = Buffer
            .from(id.toString({ urlSafe: false }), "base64")
            .toString("hex")
            .toUpperCase()
            .replaceAll(/(?<=^(.{3}){1,})(?!$)/g, "-")

        // 
        for (const product of this.stock.products) {
            this.differencesBom.addLineItem(product, 0)
        }
    }

    serialize() {
        // 
        const systemBuffer = new BitBuffer({ size: 3 })
        systemBuffer.write(1, { size: 8 }) // roof type (epdm, felt etc.)
        systemBuffer.write(1, { size: 16 }) // system/brand name (eg. one piece)

        const serializedString = systemBuffer.toString() + "." +
            serializeConfig(this.config, epdmConfigSerialOrder) + "." +
            this.deck.serialize() + "." +
            [...this.extras.entries()].reduce((acc, [code, quantity]) => {
                if (quantity <= 0) { return acc }
                return `${acc}${code}#${quantity.toString().replace(".", "-")},`
            }, "").replace(/,$/, "")

        const normalizedBuffer = new BitBuffer({
            size: serializedString.length
        })
        for (let i = 0; i < serializedString.length; i++) {
            normalizedBuffer.write(serializedString.charCodeAt(i), { size: 8 })
        }

        return normalizedBuffer.toString()
    }

    #updateConfig() {
        let trim, membrane
        for (const product of this.stock.products) {
            // 
            const trimMatch = /** @type {string} */ (product.code)
                .match(/^E([A-Z])(?<material>[A-Z])(?<color>[A-Z])_(\d{3})$/)
            if (trimMatch) {
                // 
                const { material, color } = trimMatch.groups || {}
                const option = Object.values(TRIMS).find(option => {
                    return option.color[0].toLocaleUpperCase() === color[0] &&
                        option.type[0].toLocaleUpperCase() === material[0]
                })

                // 
                if (!option) { continue }
                if (!trim || option.priority < trim.priority) { trim = option }
            }

            // 
            const membraneMatch = /** @type {string} */ (product.code)
                .match(/^M(?<type>[A-Z])_(?<thickness>\d{3})_(\d{4})$/)
            if (membraneMatch) {
                // 
                const { type, thickness } = membraneMatch.groups || {}
                const matchType = type === "F" ? MEMBRANE_TYPES.fleeceBacked
                        : MEMBRANE_TYPES.onePiece
                const option = Object.values(MEMBRANES).find(option => {
                    return matchType === option.type &&
                        option.thickness === parseInt(thickness) / MM_IN_METRE
                })

                // 
                if (!option) { continue }
                if (!membrane || option.priority < membrane.priority) {
                    membrane = option
                }
            }
        }

        // 
        if (trim) {
            this.config.trims.type = trim.type
            this.config.trims.color = trim.color
        }
        if (membrane) { this.config.membrane = membrane }
    }

    /**
     * 
     * @param {any} partitionOptions 
     * @param {object} obj
     * @param {boolean} [obj.forceFlip]
     * @returns 
     */
    #getBom(partitionOptions, { forceFlip = false } = {}) {
        this.stock.resetRunningStockQuantities()

        const { substrate, membrane: { type: membraneType } } = this.config
        if (
            substrate === SUBSTRATE_TYPES.felt &&
            membraneType !== MEMBRANE_TYPES.fleeceBacked
        ) {
            throw new Error("Incorrect membrane type for substrate")
        }

        const { stock, config } = this
        const adhesives = new AdhesiveCalculator(stock, config)
        const tapes = new TapeCalculator(stock, config, adhesives)
        const membranes = new MembraneCalculator(stock, config, adhesives, tapes)
        const trims = new TrimCalculator(stock, config, adhesives, tapes)
        const corners = new CornerCalculator(stock, config, tapes)
        const extras = new ExtraCalculator(stock, config, adhesives, tapes)

        //
        // correct deck polygon according to apexes
        // const flatPolygon - implement flatten on deck class 
        // const deckPolygon = this.deck.flatten()
        const deckPolygon = this.deck.copy()
        deckPolygon.dissolveVertices()
        const edgePolygon = deckPolygon.copy()

        //
        const joinTapeType =
            membraneType === MEMBRANE_TYPES.fleeceBacked ? TAPE_TYPES.cured
                : this.config.rubberJoin === JOIN_TYPES.seam ? TAPE_TYPES.seam
                : TAPE_TYPES.cured

        //
        let internalJoinLength = 0
        const zones = deckPolygon.partitionPolygon(partitionOptions).map(rectangle => {
            let { width, height } = rectangle
            for (const [index, edge] of rectangle.edges.array.entries()) {
                const overhang = this.deck
                    .getEdgeOverhang(edge, this.config) * 100
                // console.log("OVERHANG", overhang)
                index % 2 ? width += overhang : height += overhang

                // Continue for fleece backed membranes with pre-applied seam
                // tape along one edge.
                if (
                    index % 2 === 1 &&
                    membraneType === MEMBRANE_TYPES.fleeceBacked
                ) { continue }

                const internalJoin = this.deck
                    .getEdgeNonIntersectingLength(edge) / 100
                if (internalJoin) {
                    // console.log("HERE", this.deck.edges.indexOfValue(edge))
                    let overhangJoin = 0
                    for (const { value } of this.deck.edges) {
                        if (!edge.intersects(value)) { continue }
                        overhangJoin += this.deck
                            .getEdgeOverhang(value, this.config)
                    }
                    // console.log(internalJoin, overhangJoin)

                    internalJoinLength += internalJoin + overhangJoin
                }

                // internalJoinLength += this
                //     .deck
                //     .getEdgeNonIntersectingLength(edge) / 100
            }

            return { width, depth: height, zone: rectangle }
        })

        // console.log(internalJoinLength)

        //
        for (const { width, depth, zone } of zones) {
            // console.log(width, depth)
            const canFlip = this.config.seamDirection === SEAM_DIRECTIONS.any &&
                membraneType === MEMBRANE_TYPES.onePiece
            membranes.addDeckZone(
                width / 100,
                depth / 100,
                zone,
                { canFlip, forceFlip }
            )
        }

        if (internalJoinLength > 0) {
            tapes.addTape(internalJoinLength / 2, joinTapeType)
        }

        const contactEdgeOverhangs = this.deck.edges.array.map(edge => {
            const nonContactEdgeTypes = /** @type {EdgeType[]} */ ([
                "raisedKerb",
                "gutterDrip",
                "none",
            ])

            return nonContactEdgeTypes.includes(this.deck.getEdgeType(edge))
                ? 0 : this.deck.getEdgeOverhang(edge, this.config) * 100
        })

        const contactDeckArea = edgePolygon.getBorderArea(this.config.contactBorderDepth * 100, "internal") / 10000
        const contactUpstandArea = edgePolygon.getBorderArea(contactEdgeOverhangs, "external") / 10000

        const contactTotalArea = contactDeckArea + contactUpstandArea
        const internalDeckArea = this.deck.area / 10000 - contactDeckArea

        adhesives.addAdhesive(
            ADHESIVE_TYPES.deck.contactBonding,
            contactTotalArea
        )
        if (config.substrate === SUBSTRATE_TYPES.felt) {
            adhesives.addAdhesive(
                ADHESIVE_TYPES.deck.pu,
                internalDeckArea
            )
        }
        else if (
            config.substrate === SUBSTRATE_TYPES.grp ||
            config.substrate === SUBSTRATE_TYPES.metal ||
            this.deck.area >= 100 * 10000
        ) {
            adhesives.addAdhesive(
                ADHESIVE_TYPES.deck.contactBonding,
                internalDeckArea
            )
        }
        else {
            adhesives.addAdhesive(
                ADHESIVE_TYPES.deck.waterBased,
                internalDeckArea
            )
        }

        for (const { value: edge } of this.deck.edges) {
            const node = this.deck.edges.nodeWithValue(edge)
            if (!node) { throw new Error() }

            const nextEdge = node.next?.value
            if (!nextEdge) { throw new Error() }

            const edgeType = this.deck.getEdgeType(edge)
            const nextEdgeType = this.deck.getEdgeType(nextEdge)
            const angle = this.deck.internalAngleAtVertex(nextEdge.start)

            trims.addTrim(edgeType, edge.magnitude / 100)
            corners.addCorner(edgeType, nextEdgeType, angle)
        }

        for (const [code, quantity] of this.extras) {
            extras.addExtra(code, quantity)
        }

        // 1. Copy internal polygon and subdivide into rectangles.
        // 2. Infer expanded deck zone sizes from deck edge overhangs.
        // 3. Get membrane options for each deck zone in turn, using running
        // stock tracker between each zone.
        // 4. Check rubber joins between zones, and add required tapes etc.
        // 5. Add trims and corners for every edge.
        // 6. Add patches for any rooflights.
        // 7. Add required patches for any drains.
        // 8. Add extras.
        // 9. Return bom.

        const bom = this.stock.getQuantityRectifiedBom(Bom.join([
            adhesives.bom,
            tapes.bom,
            membranes.bom,
            trims.bom,
            corners.bom,
            extras.bom
        ]))

        return bom
    }

    /**
     * 
     * @param {object} obj
     * @param {boolean} [obj.suppressErrors] Should  
     */
    updateBom({ suppressErrors = false } = {}) {
        // Build all partition options 
        const { seamDirection } = this.config
        const partitionOptions = [
            { isCrossAxis: false, shouldExpand: false },
            { isCrossAxis: false, shouldExpand: true },
            { isCrossAxis: true, shouldExpand: false },
            { isCrossAxis: true, shouldExpand: true }
        ].filter(({ isCrossAxis, shouldExpand }) => {
            return seamDirection === SEAM_DIRECTIONS.any ? true
                : shouldExpand ? false
                : seamDirection === SEAM_DIRECTIONS.vertical === isCrossAxis
        })

        // Build 
        const boms = []
        const forceFlip = seamDirection === SEAM_DIRECTIONS.horizontal
        for (const partitionOption of partitionOptions) {
            // 
            try { boms.push(this.#getBom(partitionOption, { forceFlip })) }
            catch (error) { if (!suppressErrors) { throw error } }

            // Adding forced flip and forced no flip independently to
            // calculate all fleece backed options running in all seam
            // directions.
            if (seamDirection === SEAM_DIRECTIONS.any) {
                try {
                    boms.push(this.#getBom(partitionOption, {
                        forceFlip: !forceFlip
                    }))
                }
                catch (error) { if (!suppressErrors) { throw error } }
            }
        }

        // Fetch required bom according to relevant filters from 
        const requiredBom = boms.sort((bomA, bomB) => {
            return bomA.membraneCount - bomB.membraneCount ||
                bomA.price - bomB.price
        })[0]
        this.requiredBom.clearLineItems()
        this.requiredBom.append(requiredBom)
    }

    /**
     * 
     */
    // get bom() {
    //     const { seamDirection } = this.config
    //     const partitionOptions = [
    //         { isCrossAxis: false, shouldExpand: false },
    //         { isCrossAxis: false, shouldExpand: true },
    //         { isCrossAxis: true, shouldExpand: false },
    //         { isCrossAxis: true, shouldExpand: true }
    //     ].filter(({ isCrossAxis, shouldExpand }) => {
    //         return seamDirection === SEAM_DIRECTIONS.any ? true
    //             : shouldExpand ? false
    //             : !(seamDirection === SEAM_DIRECTIONS.vertical !== isCrossAxis)
    //     })

    //     const boms = []
    //     for (const partitionOption of partitionOptions) {
    //         // Adding forced flip and forced no flip independently to calculate
    //         // all fleece backed options running in all same direction.
    //         let forceFlip = seamDirection === SEAM_DIRECTIONS.horizontal
    //         boms.push(this.#getBom(partitionOption, { forceFlip }))
    //         if (seamDirection === SEAM_DIRECTIONS.any) {
    //             forceFlip = !forceFlip
    //             boms.push(this.#getBom(partitionOption, { forceFlip }))
    //         }
    //     }

    //     return boms.sort((bomA, bomB) => {
    //         return bomA.membraneCount - bomB.membraneCount ||
    //             bomA.price - bomB.price
    //     })[0]
    // }

    get bom() {
        return Bom.join([this.requiredBom, this.differencesBom])
    }

    // get categorisedBoms

    /**
     * 
     * @param {string} normalizedString
     * @param {Stock} stock
     * @returns {EpdmCalculator}
     */
    static fromNormalized(normalizedString, stock) {
        // 
        let serializedString = ""
        const normalizedBuffer = BitBuffer.from(normalizedString)
        while (normalizedBuffer.readPointer < normalizedBuffer.bitLength) {
            // Fetch char code, breaking if char code is falsy (0 or NaN), as
            // this indicates that the end of the buffer has been reached, or
            // that the padding end null bits have been reached.
            const charCode = normalizedBuffer.read(8)
            if (!charCode) { break }

            serializedString += String.fromCharCode(charCode)
        }

        // 
        const [
            serializedSystem,
            serializedConfig,
            serializedDeck,
            serializedExtras
        ] = serializedString.split(".")

        // 
        const config = deserializeConfig(
            serializedConfig,
            JSON.parse(JSON.stringify(epdmConfig)), // safe as config is a strictly JSON object 
            epdmConfigSerialOrder
        )
        const deck = Deck.fromSerialized(serializedDeck)
        stock.calculatorConfig = config

        const calculator = new EpdmCalculator(deck, stock, { config })

        if (serializedExtras) {
            for (const extra of serializedExtras.split(",")) {
                const [code, hyphenQuantity] = extra.split("#")
                const quantity = parseFloat(hyphenQuantity.replace("-", "."))
                calculator.extras.set(code, quantity)
            }
        }

        return calculator
    }
}

// @@exports
export { EpdmCalculator }
